import Swiper from "swiper";
import { EffectFade, Navigation } from "swiper/modules";
import type { SwiperOptions } from "swiper/types";
import { isRtlDocument } from "./timeline-utils";
import { DETAIL_EVENT_IMAGE_SWIPER_SPEED_MS } from "./timeline-constants";

export type DetailEventImageSwiperElement = HTMLElement & {
	_imageSwiper?: Swiper;
};

export function destroyDetailEventImageSwipers(container: HTMLElement): void {
	container
		.querySelectorAll<DetailEventImageSwiperElement>(".historical-timeline__detail-event-media-swiper")
		.forEach((swiperEl) => {
			if (swiperEl._imageSwiper && !swiperEl._imageSwiper.destroyed) {
				swiperEl._imageSwiper.destroy(true, true);
			}

			swiperEl._imageSwiper = undefined;
		});
}

function updateDetailEventImageCaption(swiperEl: HTMLElement, activeIndex: number): void {
	const captionsHost = swiperEl
		.closest(".historical-timeline__detail-event-media-swiper-wrap")
		?.querySelector<HTMLElement>(".historical-timeline__detail-event-media-captions");

	if (!captionsHost) {
		return;
	}

	captionsHost.querySelectorAll<HTMLElement>(".historical-timeline__detail-event-caption").forEach((caption, index) => {
		caption.classList.toggle("is-active", index === activeIndex);
	});
}

export function initDetailEventImageSwipers(container: HTMLElement): void {
	destroyDetailEventImageSwipers(container);

	container
		.querySelectorAll<DetailEventImageSwiperElement>(".historical-timeline__detail-event-media-swiper")
		.forEach((swiperEl) => {
			const imageStage = swiperEl.closest<HTMLElement>(".historical-timeline__detail-event-media-image-stage");
			const prevButton = imageStage?.querySelector<HTMLElement>(".swiper-button-prev");
			const nextButton = imageStage?.querySelector<HTMLElement>(".swiper-button-next");
			const slideCount = swiperEl.querySelectorAll(".swiper-slide").length;
			const hasMultipleSlides = slideCount > 1;

			if (!hasMultipleSlides) {
				return;
			}

			if (isRtlDocument()) {
				swiperEl.setAttribute("dir", "rtl");
			}

			const swiperConfig: SwiperOptions = {
				modules: [Navigation, EffectFade],
				effect: "fade",
				fadeEffect: {
					crossFade: true,
				},
				slidesPerView: 1,
				loop: true,
				speed: DETAIL_EVENT_IMAGE_SWIPER_SPEED_MS,
				allowTouchMove: false,
				simulateTouch: false,
				watchOverflow: true,
				navigation:
					prevButton && nextButton
						? {
							prevEl: prevButton,
							nextEl: nextButton,
							// Custom CSS icons via span; prevent Swiper v12+ default SVG injection
							addIcons: false,
						}
						: false,
				on: {
					slideChange: (swiper) => {
						updateDetailEventImageCaption(swiperEl, swiper.realIndex);
					},
				},
			};

			const swiper = new Swiper(swiperEl, swiperConfig);
			swiperEl._imageSwiper = swiper;
			updateDetailEventImageCaption(swiperEl, swiper.realIndex);
		});
}
